import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { resolveOrgIcon } from '../lib/orgIcons';
import { pluralize } from '@/shared/lib/format';

interface CoordinationCardProps {
  id: string;
  name: string;
  description: string | null;
  /** Slug de `coordinations.icon`; desconhecido ou nulo cai no ícone neutro. */
  icon: string | null;
  ministryCount: number;
}

/**
 * Uma coordenação na grade da aba Coordenações.
 *
 * O card responde **o que a aba responde**: como a igreja está organizada —
 * o que a coordenação faz e de que tamanho ela é. Nada sobre projeto.
 *
 * ⚠️ **As pills de situação saíram daqui** (ação 6 da
 * [ADR-012](../../../../docs/adr/ADR-012-navegacao-por-caso-de-uso.md)), e a
 * razão é a mesma que tirou os `StatusTiles` do topo da tela: a contagem chega
 * recortada pela `projects_select`, então o card de uma coordenação vizinha
 * dizia "nenhum projeto visível para você" com a tipografia de um dado sobre a
 * coordenação. Um número que fala do LEITOR usando a moldura de quem é lido é
 * pior do que nenhum número. Atividade de projeto tem tela própria — `/projetos`
 * e o Calendário —, e as duas mostram o mesmo recorte com a moldura certa.
 *
 * ⚠️ **Os NOMES dos ministérios também saíram** (23/08/2026). O card mostrava
 * "3 ministérios · Louvor · Mídia e Transmissão · +1", e a lista era o que
 * autorizava fechar o acordeão da versão anterior desta tela. Ela cai por dois
 * motivos que só apareceram com a grade montada: em 375px a linha embrulhava em
 * três, virando o elemento mais alto de um card cujo assunto é a COORDENAÇÃO; e
 * um nome truncado no meio de um "+N" convida ao clique para descobrir o resto,
 * que é exatamente o clique que a tela de detalhe já resolve melhor. O que
 * sobrevive é o número — a resposta de "de que tamanho é isto" cabe num
 * inteiro, e o inteiro não embrulha.
 *
 * **A faixa colorida do topo não é enfeite.** Sete cards de mesma altura,
 * mesmo peso e mesma cor tornam a grade um paredão em que o olho não acha onde
 * parar; a faixa dá um ponto de fixação por card e um lugar para o emblema. Ela
 * usa o azul da marca (`primary` → `primary-hover`) e **só ele**: o painel gasta
 * matiz para dizer SITUAÇÃO de projeto (spec-calendar-ux), e uma faixa verde ou
 * âmbar aqui seria lida como estado de alguma coisa.
 *
 * O `<Link>` cobre o card inteiro (não só o título) porque em 375px o alvo de
 * toque é o cartão — é o mesmo padrão do `MinistryRow`.
 */
export function CoordinationCard({
  id,
  name,
  description,
  icon,
  ministryCount,
}: CoordinationCardProps) {
  const Icon = resolveOrgIcon(icon);

  return (
    <li>
      <Link
        to={`/coordenacoes/${id}`}
        className="group flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors duration-fast ease-out hover:bg-accent/60 focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
      >
        {/* Decorativa de ponta a ponta: nome, contagem e destino já estão no
            texto abaixo, então a faixa não acrescenta nada a quem usa leitor
            de tela — e um `img` sem conteúdo só atrapalharia a leitura. */}
        <div
          className="h-7 bg-gradient-to-br from-primary to-primary-hover"
          aria-hidden="true"
        />

        <div className="flex flex-1 items-start gap-3 p-4">
          {/* O emblema sobe para dentro da faixa e leva a borda do card junto:
              é o que costura as duas metades em vez de deixar uma tarja azul
              solta sobre um branco. Com a faixa em 28px o recuo é de 28px (e
              não os 44px de quando ela tinha 56px): o círculo entra ~12px nela
              e ainda sobra faixa visível acima — engolir a faixa inteira faria
              a cor virar contorno do ícone. */}
          <div className="-mt-7 flex size-12 shrink-0 items-center justify-center rounded-full border border-border bg-card shadow-sm">
            <Icon className="size-6 text-primary" aria-hidden="true" />
          </div>

          <div className="min-w-0 flex-1 space-y-1.5">
            {/* Três degraus de tipografia, e a ordem é a das perguntas: QUEM é
                (nome, o único peso forte), O QUE faz (descrição, corpo em
                cinza) e DE QUE TAMANHO é (contagem, o menor, em caixa alta —
                rótulo, não frase). Antes os três estavam em `text-sm`, dois
                deles com a mesma cor, e o card era um bloco de texto com um
                título por cima. */}
            <p className="line-clamp-2 text-lg font-semibold leading-tight tracking-tight">{name}</p>

            {/* Coordenação sem descrição não ganha linha vazia nem texto de
                recheio: a estrutura cadastrada antes desta coluna existir é
                legítima, e um "Sem descrição" repetido em sete cards seria
                ruído com aparência de pendência. */}
            {description ? (
              <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                {description}
              </p>
            ) : null}

            <p className="tabular pt-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {ministryCount === 0
                ? 'Nenhum ministério cadastrado'
                : pluralize(ministryCount, 'ministério', 'ministérios')}
            </p>
          </div>

          {/* A seta anda 2px para a direita no hover — o mesmo movimento do
              `MinistryRow`, e pela mesma razão: dizer que o card LEVA a algum
              lugar sem gastar uma palavra. */}
          <ChevronRight
            className="-mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform duration-fast ease-out group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </div>
      </Link>
    </li>
  );
}
